import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface Node {
  id: string;
  name: string;
  location: string;
  joined: Date;
  projects: string[];
  skills: string[];
  x: number;
  y: number;
  connections: string[];
  avatar: string;
  isYou?: boolean;
  status: 'online' | 'building' | 'offline';
}

interface Connection {
  from: string;
  to: string;
  strength: number;
  reason: string;
}

const CANVAS_WIDTH = 1200;
const CANVAS_HEIGHT = 800;

export const GraphApp: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [nodes, setNodes] = useState<Node[]>([]);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [yourNode, setYourNode] = useState<Node | null>(null);

  // Generate mock network data
  useEffect(() => {
    const locations = ['San Francisco', 'New York', 'London', 'Tokyo', 'Berlin', 'Sydney', 'Toronto', 'Singapore', 'Mumbai', 'São Paulo'];
    const skills = ['React', 'Node.js', 'Python', 'Go', 'Rust', 'TypeScript', 'GraphQL', 'Docker', 'AWS', 'CloudFlare'];
    const names = [
      'Alex Chen', 'Sarah Kim', 'Mike Rodriguez', 'Emma Wilson', 'David Thompson',
      'Lisa Park', 'Carlos Silva', 'Sophie Brown', 'James Taylor', 'Maria Garcia',
      'Ahmed Hassan', 'Nina Patel', 'Lucas Meyer', 'Yuki Tanaka', 'Anna Kowalski'
    ];

    const mockNodes: Node[] = names.map((name, i) => {
      const isYou = i === 0;
      return {
        id: `node-${i}`,
        name: isYou ? 'You' : name,
        location: locations[i % locations.length],
        joined: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000),
        projects: ['Global API', 'Momentum App', 'Graph Network'].slice(0, Math.floor(Math.random() * 3) + 1),
        skills: skills.slice(0, Math.floor(Math.random() * 5) + 2),
        x: Math.random() * (CANVAS_WIDTH - 100) + 50,
        y: Math.random() * (CANVAS_HEIGHT - 100) + 50,
        connections: [],
        avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${name}`,
        isYou,
        status: Math.random() > 0.7 ? 'online' : Math.random() > 0.5 ? 'building' : 'offline'
      };
    });

    // Generate connections based on proximity and shared interests
    const mockConnections: Connection[] = [];
    mockNodes.forEach((node, i) => {
      const numConnections = Math.floor(Math.random() * 4) + 2;
      const potentialConnections = mockNodes
        .filter((other, j) => j !== i && !mockConnections.find(c => 
          (c.from === node.id && c.to === other.id) || (c.from === other.id && c.to === node.id)
        ))
        .sort(() => Math.random() - 0.5)
        .slice(0, numConnections);

      potentialConnections.forEach(other => {
        const sharedSkills = node.skills.filter(skill => other.skills.includes(skill));
        const connection: Connection = {
          from: node.id,
          to: other.id,
          strength: Math.min(sharedSkills.length / 3, 1),
          reason: sharedSkills.length > 0 
            ? `Shared: ${sharedSkills.slice(0, 2).join(', ')}`
            : `Both from ${node.location === other.location ? node.location : 'similar timezone'}`
        };
        mockConnections.push(connection);
        node.connections.push(other.id);
        other.connections.push(node.id);
      });
    });

    setNodes(mockNodes);
    setConnections(mockConnections);
    setYourNode(mockNodes[0]);
  }, []);

  // Animate your node connection
  useEffect(() => {
    if (yourNode && !isConnecting) {
      const timer = setTimeout(() => {
        setIsConnecting(true);
        // Simulate connecting your node to the network
        setTimeout(() => {
          setIsConnecting(false);
        }, 3000);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [yourNode, isConnecting]);

  // Draw the network on canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const draw = () => {
      ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

      // Draw connections
      connections.forEach(connection => {
        const fromNode = nodes.find(n => n.id === connection.from);
        const toNode = nodes.find(n => n.id === connection.to);
        if (!fromNode || !toNode) return;

        ctx.beginPath();
        ctx.moveTo(fromNode.x, fromNode.y);
        ctx.lineTo(toNode.x, toNode.y);
        ctx.strokeStyle = `rgba(99, 102, 241, ${0.1 + connection.strength * 0.3})`;
        ctx.lineWidth = 1 + connection.strength * 2;
        ctx.stroke();
      });

      // Draw nodes
      nodes.forEach(node => {
        // Node glow for online status
        if (node.status === 'online') {
          ctx.beginPath();
          ctx.arc(node.x, node.y, 15, 0, 2 * Math.PI);
          ctx.fillStyle = 'rgba(34, 197, 94, 0.2)';
          ctx.fill();
        }

        // Main node circle
        ctx.beginPath();
        ctx.arc(node.x, node.y, 8, 0, 2 * Math.PI);
        
        if (node.isYou && isConnecting) {
          ctx.fillStyle = '#06b6d4';
          ctx.shadowColor = '#06b6d4';
          ctx.shadowBlur = 10;
        } else if (node.status === 'online') {
          ctx.fillStyle = '#22c55e';
        } else if (node.status === 'building') {
          ctx.fillStyle = '#0ea5e9';
        } else {
          ctx.fillStyle = '#6b7280';
        }
        
        ctx.fill();
        ctx.shadowBlur = 0;

        // Special highlighting for your node
        if (node.isYou) {
          ctx.beginPath();
          ctx.arc(node.x, node.y, 12, 0, 2 * Math.PI);
          ctx.strokeStyle = '#06b6d4';
          ctx.lineWidth = 2;
          ctx.stroke();
        }
      });
    };

    draw();
    const interval = setInterval(draw, 100);
    return () => clearInterval(interval);
  }, [nodes, connections, isConnecting]);

  const handleCanvasClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    const clickedNode = nodes.find(node => {
      const distance = Math.sqrt((x - node.x) ** 2 + (y - node.y) ** 2);
      return distance <= 12;
    });

    if (clickedNode) {
      setSelectedNode(clickedNode);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online': return 'text-green-400';
      case 'building': return 'text-yellow-400';
      default: return 'text-gray-400';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'online': return 'Online';
      case 'building': return 'Building';
      default: return 'Offline';
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      {/* Header */}
      <div className="relative overflow-hidden bg-blue-600 p-6">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-6xl mx-auto"
        >
          <h1 className="text-4xl font-bold mb-2">🌐 Shipping School Network</h1>
          <p className="text-xl opacity-90">You're now connected to builders worldwide</p>
        </motion.div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Network Stats */}
          <div className="lg:col-span-1 space-y-4">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-slate-800/50 backdrop-blur rounded-xl p-6"
            >
              <h3 className="text-xl font-bold mb-4">Network Stats</h3>
              <div className="space-y-3">
                <div>
                  <div className="text-2xl font-bold text-blue-400">{nodes.length}</div>
                  <div className="text-sm opacity-75">Total Builders</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-400">
                    {nodes.filter(n => n.status === 'online').length}
                  </div>
                  <div className="text-sm opacity-75">Online Now</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-orange-400">
                    {connections.length}
                  </div>
                  <div className="text-sm opacity-75">Active Connections</div>
                </div>
              </div>
            </motion.div>

            {isConnecting && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-orange-600 rounded-xl p-6 text-center"
              >
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                  className="w-8 h-8 border-2 border-white border-t-transparent rounded-full mx-auto mb-3"
                />
                <h3 className="font-bold mb-2">Connecting You...</h3>
                <p className="text-sm opacity-90">
                  Finding your place in the network
                </p>
              </motion.div>
            )}

            {yourNode && !isConnecting && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-blue-600 rounded-xl p-6"
              >
                <h3 className="font-bold mb-2">🎉 You're Connected!</h3>
                <p className="text-sm opacity-90 mb-3">
                  Your node is now part of the network
                </p>
                <div className="text-xs opacity-75">
                  Connected to: {yourNode.connections.length} builders
                </div>
              </motion.div>
            )}
          </div>

          {/* Network Visualization */}
          <div className="lg:col-span-3">
            <div className="bg-slate-800/50 backdrop-blur rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold">Live Network Graph</h2>
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                    <span>Online</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                    <span>Building</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-blue-500 rounded-full ring-2 ring-blue-300"></div>
                    <span>You</span>
                  </div>
                </div>
              </div>
              
              <div className="relative">
                <canvas
                  ref={canvasRef}
                  width={CANVAS_WIDTH}
                  height={CANVAS_HEIGHT}
                  onClick={handleCanvasClick}
                  className="w-full h-auto bg-slate-900/50 rounded-lg cursor-pointer"
                  style={{ aspectRatio: `${CANVAS_WIDTH}/${CANVAS_HEIGHT}` }}
                />
                
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1 }}
                  className="absolute top-4 left-4 bg-slate-800/80 backdrop-blur rounded-lg p-3 text-sm"
                >
                  <p className="opacity-75">Click any node to see details</p>
                </motion.div>
              </div>
            </div>
          </div>
        </div>

        {/* Node Details Modal */}
        <AnimatePresence>
          {selectedNode && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur flex items-center justify-center z-50"
              onClick={() => setSelectedNode(null)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-slate-800 rounded-xl p-6 max-w-md w-full mx-4"
              >
                <div className="flex items-center gap-3 mb-4">
                  <img
                    src={selectedNode.avatar}
                    alt={selectedNode.name}
                    className="w-12 h-12 rounded-full"
                  />
                  <div>
                    <h3 className="text-xl font-bold">{selectedNode.name}</h3>
                    <p className="text-sm opacity-75">{selectedNode.location}</p>
                  </div>
                  <div className={`ml-auto ${getStatusColor(selectedNode.status)}`}>
                    <div className="text-sm font-medium">
                      {getStatusText(selectedNode.status)}
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold mb-2">Projects Shipped</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedNode.projects.map((project, i) => (
                        <span
                          key={i}
                          className="px-2 py-1 bg-blue-600/30 text-blue-200 rounded text-sm"
                        >
                          {project}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2">Skills</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedNode.skills.map((skill, i) => (
                        <span
                          key={i}
                          className="px-2 py-1 bg-blue-600/30 text-blue-200 rounded text-sm"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2">Network</h4>
                    <p className="text-sm opacity-75">
                      Connected to {selectedNode.connections.length} builders
                    </p>
                    <p className="text-xs opacity-50">
                      Joined {selectedNode.joined.toLocaleDateString()}
                    </p>
                  </div>

                  {!selectedNode.isYou && (
                    <button className="w-full bg-blue-600 text-white py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors">
                      Connect & Collaborate
                    </button>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5 }}
          className="mt-8 bg-blue-600 rounded-xl p-8 text-center"
        >
          <h3 className="text-2xl font-bold mb-2">🎓 Welcome to the Network</h3>
          <p className="text-lg opacity-90 mb-6">
            You're now part of a global community of builders who ship real applications
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-blue-600 px-6 py-3 rounded-lg font-bold hover:bg-gray-100 transition-colors">
              Explore Collaborations
            </button>
            <button className="border border-white text-white px-6 py-3 rounded-lg font-bold hover:bg-white/10 transition-colors">
              Share Your Success
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};